
/**
 * Module dependencies.
 */

import { IntersectionOptions, useInView } from 'react-intersection-observer';
import { ifProp, theme } from 'styled-tools';
import { kioskRoutes } from 'src/core/routes';
import { useRouter } from 'next/router';
import React, { ElementType, ReactElement, ReactNode, useMemo } from 'react';
import merge from 'lodash/merge';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  as?: ElementType,
  children: ReactNode,
  className?: string,
  options?: IntersectionOptions
};

/**
 * `Content` styled component.
 */

const Content = styled.div<{ isVisible: boolean }>`
  transition:
    opacity 0.6s ease-out,
    transform 0.6s ${theme('animations.easeInOutSine')};
  will-change: opacity, transform;

  ${ifProp('isVisible', css`
    opacity: 1;
    transform: none;
  `, css`
    opacity: 0;
    transform: translateY(25%);
  `)}
`;

/**
 * `FadeInUpAnimation` component.
 */

const FadeInUpAnimation = (props: Props): ReactElement => {
  const { children, options, ...rest } = props;
  const router = useRouter();
  const [ref, inView] = useInView(merge({}, {
    threshold: 0.2,
    triggerOnce: true
  }, options));

  const isKiosk = useMemo(() => {
    return router.asPath.includes(kioskRoutes.home);
  }, [router]);

  return (
    <Content
      {...rest}
      {...!isKiosk && { ref }}
      isVisible={isKiosk ? true : inView}
    >
      {children}
    </Content>
  );
};

/**
 * Export `FadeInUpAnimation` component.
 */

export default FadeInUpAnimation;
